import * as React from 'react';
import {useDispatch} from 'react-redux';

import {convertToArray, safelyGetFilterByKey} from 'nutshell-core/filter';

import {DashboardCard} from 'shells/dashboard/card/dashboard-card';
import {List} from 'shells/dashboard/list/list';
import {Illustration} from 'shells/illustrations/illustration';
import {ChangeRow} from 'shells/metrics/change-row';
import {Metric} from 'shells/metrics/metric';
import {PromoStateOverlay} from 'shells/promo-state-overlay/promo-state-overlay';

import * as actions from '../../../connected-forms/connected-forms-actions';
import {OpenableModals, useOpenableModal} from '../../../openable-modals';
import type {ReportURLParams} from '../../../reports/report-types';

import {DEFAULT_TIMESPAN} from '../dashboard';
import {useGetConnectedFormsDashboard} from '../graphql/hooks';
import {mapViewSubmissionItemToListItem} from '../helpers';
import {ViewAllLink} from '../view-all-link';

import {ConnectedFormsChart} from './report/connected-forms-chart';

import styles from './connected-forms-card.module.css';

type Props = {
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
};

export function ConnectedFormsCard(props: Props) {
    const dispatch = useDispatch();
    const activeFilters = convertToArray(props.urlParams.filter);
    const dateRangeFilter = safelyGetFilterByKey(activeFilters, 'period');
    const {onOpen: onOpenConnectedFormsModal} = useOpenableModal(
        OpenableModals.CONNECTED_FORMS_INFO_MODAL
    );
    const {onOpen: onOpenAnalyticsEmbedModal} = useOpenableModal(
        OpenableModals.EMBED_ANALYTICS_MODAL
    );

    const {connectedFormsDashboard, isLoading} = useGetConnectedFormsDashboard({
        timespan: dateRangeFilter || DEFAULT_TIMESPAN,
    });

    const stat = connectedFormsDashboard ? connectedFormsDashboard.stat : undefined;
    const topForms = connectedFormsDashboard ? connectedFormsDashboard.topForms : [];
    const listItems = topForms.map((data) =>
        mapViewSubmissionItemToListItem({
            ...data.form,
            views: data.views,
            submissions: data.submissions,
        })
    );
    const numForms = connectedFormsDashboard ? connectedFormsDashboard.numForms : undefined;

    // hardcoded since we're not using the url to feed this query
    const reportParams = {
        dateRangeType: 'period',
        facet: 'count',
        gap: 'd',
        segment: 'interactionType',
    } as const;

    React.useEffect(() => {
        dispatch(
            actions.requestReportData({
                reportParams,
                filters: activeFilters,
                formType: 1,
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    return (
        <DashboardCard
            title='Connected forms'
            subtitle='Collect leads from any form on your website'
            link={<ViewAllLink totalCount={numForms} type='CONNECTED_FORMS' />}
            isLoading={isLoading}
            mainContent={{
                content: (
                    <div className='flex flex-dir-col full-width full-height gap-8 pt-16 pos-relative'>
                        <div className={styles.header}>
                            <Metric
                                title='Total submissions from all connected forms'
                                value={stat ? stat.formattedValue : '0'}
                                bottomRow={
                                    stat &&
                                    stat.delta && (
                                        <ChangeRow
                                            percentChange={stat.delta.percentChange}
                                            timespanSuffix={stat.delta.timespanSuffix || ''}
                                            changeType={stat.delta.direction}
                                        />
                                    )
                                }
                                isCentered={true}
                                // @ts-expect-error
                                isLarge={true}
                            />
                        </div>
                        <div className={styles.chart}>
                            <ConnectedFormsChart
                                activeFilters={activeFilters}
                                height={270}
                                containerHeight={270}
                                onParamChange={props.onParamChange}
                                reportParams={reportParams}
                                hasBorder={false}
                                hasGap={false}
                            />
                        </div>
                    </div>
                ),
            }}
            sideContentProps={{
                content: (
                    <List
                        title='Top submission'
                        subtitle='Your most submitted connected forms'
                        listItems={listItems}
                        emptyStateText='No connected forms have been submitted'
                    />
                ),
            }}
            promoStateOverlay={
                numForms === 0 ? (
                    <PromoStateOverlay
                        promoStateProps={{
                            isHorizontal: false,
                            imageComponent: (
                                <Illustration src='trial-onboarding/marketing-connected-forms' />
                            ),
                            title: 'Connect your existing forms to Nutshell',
                            subtitle:
                                'Capture submissions from any form on your website, including those created with other tools.',
                            primaryCta: {
                                text: 'Learn more',
                                onClick: onOpenConnectedFormsModal,
                            },
                            secondaryCta: {
                                text: 'Set up tracking',
                                onClick: onOpenAnalyticsEmbedModal,
                            },
                            blueImageBackground: true,
                        }}
                        align='right'
                    />
                ) : null
            }
        />
    );
}
